
import { useEffect, useCallback, useRef, useState } from 'react';
import { Session } from '@supabase/supabase-js';
import { 
  shouldRefreshSession, 
  performSessionRefresh, 
  setupSessionRefresh,
  isSessionValid,
  isSessionNearExpiry,
  detectInactivity,
  updateLastActivity,
  getPersistenceType
} from '@/utils/auth/sessionManager';
import { useToast } from '@/hooks/use-toast';

type RefreshStatus = 'idle' | 'refreshing' | 'success' | 'error';

interface UseSessionRefreshResult {
  refreshSession: () => Promise<void>;
  status: RefreshStatus;
  lastRefreshed: Date | null;
}

export const useSessionRefresh = (
  session: Session | null,
  onRefreshSuccess: (session: Session) => void,
  onRefreshError: () => void
): UseSessionRefreshResult => {
  const { toast } = useToast();
  const [status, setStatus] = useState<RefreshStatus>('idle');
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const refreshingRef = useRef(false);
  const refreshAttemptRef = useRef(0);
  const unmountedRef = useRef(false);
  
  useEffect(() => {
    return () => {
      unmountedRef.current = true;
    };
  }, []);
  
  const handleRefresh = useCallback(async () => {
    // Prevent concurrent refresh attempts
    if (refreshingRef.current) {
      console.log('Session refresh already in progress, skipping');
      return;
    }
    
    try {
      refreshingRef.current = true;
      refreshAttemptRef.current += 1;
      
      if (!unmountedRef.current) setStatus('refreshing');
      
      console.log('Refreshing session...');
      const { data: { session: newSession, error } } = await performSessionRefresh();
      
      // Break if component unmounted during refresh
      if (unmountedRef.current) return;
      
      if (error) {
        console.error('Session refresh error:', error);
        
        if (error.message.includes('expired') || error.status === 401) {
          toast({
            title: "Session Expired",
            description: "Please sign in again to continue.",
            variant: "destructive",
          });
          
          setStatus('error');
          onRefreshError();
          return;
        } else {
          // For other errors, maybe we don't need to sign out the user yet
          toast({
            title: "Error Refreshing Session",
            description: "Please try signing in again if problems persist.",
            variant: "destructive",
          });
          
          setStatus('error');
          
          // Only trigger refresh error for persistent failures
          if (refreshAttemptRef.current >= 3) {
            onRefreshError();
          }
          return;
        }
      }
      
      if (newSession) {
        console.log('Session refresh successful');
        if (!unmountedRef.current) {
          setStatus('success');
          setLastRefreshed(new Date());
        }
        onRefreshSuccess(newSession);
        refreshAttemptRef.current = 0; // Reset counter on success
      } else {
        // Edge case: no session returned but also no error
        console.warn('No session returned from refresh, but no error occurred');
        if (!unmountedRef.current) setStatus('error');
        onRefreshError();
      }
    } catch (error) {
      console.error('Unexpected error during session refresh:', error);
      if (!unmountedRef.current) {
        setStatus('error');
        toast({
          title: "Authentication Error",
          description: "An unexpected error occurred. Please try signing in again.",
          variant: "destructive",
        });
      }
      onRefreshError();
    } finally {
      refreshingRef.current = false;
    }
  }, [onRefreshSuccess, onRefreshError, toast]);
  
  // Monitor for user activity to refresh token after inactivity
  useEffect(() => {
    const handleUserActivity = () => {
      updateLastActivity();
      
      // Check if we need to verify the token after inactivity
      if (session && detectInactivity()) {
        console.log('User returned after inactivity, verifying session...');
        
        // Verify the session is still valid - no need to fully refresh yet if it's valid
        if (isSessionValid(session) && !isSessionNearExpiry(session)) {
          console.log('Session still valid after inactivity');
        } else {
          console.log('Session expired or near expiry after inactivity, refreshing');
          handleRefresh();
        }
      }
    };
    
    // Monitor common user interaction events
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => window.addEventListener(event, handleUserActivity, { passive: true }));
    
    // Set initial activity timestamp
    updateLastActivity();
    
    return () => {
      events.forEach(event => window.removeEventListener(event, handleUserActivity));
    };
  }, [session, handleRefresh]);
  
  // Immediate check for session validity on mount or session change
  useEffect(() => {
    if (!session) {
      // No session to refresh
      return;
    }
    
    // Check if session is valid but near expiry or already requires refresh
    if (isSessionValid(session) && (isSessionNearExpiry(session) || shouldRefreshSession(session))) {
      console.log('Session is near expiry, refreshing immediately');
      handleRefresh();
    }
  }, [session, handleRefresh]);
  
  // Set up periodic refresh
  useEffect(() => {
    if (!session) return;
    
    // Get more frequent refresh for session storage vs local storage
    const persistenceType = getPersistenceType();
    console.log(`Setting up automatic session refresh (persistence: ${persistenceType})`);
    
    const cleanup = setupSessionRefresh(session, handleRefresh);
    
    return cleanup;
  }, [session, handleRefresh]);
  
  return { 
    refreshSession: handleRefresh,
    status,
    lastRefreshed
  };
};
